import { format } from 'date-fns';
import { z } from 'zod';
import { legalClientAccountingSchema } from './ILegalClientAccounting';
import { legalClientAddressSchema } from './ILegalClientAddress';
import { contactInformationSchema } from './ILegalClientContactInformation';
import { legalClientRelationshipSchema } from './ILegalClientRelationship/ILegalClientRelationship';
import {
  legalTransactionSchema,
  type ILegalTransaction,
} from './ILegalTransaction';
import { taskFullObjectSchema } from './ITask';
import { legalClientGroupSchema } from './ILegalClientGroup';

export interface ILegalTransactionObject {
  LegalTransaction: ILegalTransaction;
  LegalRole: string | null;
  idLegalRole: number | null;
}

export const legalTransactionObjectSchema = z.object({
  LegalTransaction: legalTransactionSchema,
  LegalRole: z.unknown().nullable().optional(),
  idLegalRole: z.number().nullable(),
});

export const legalClientSchema = z.object({
  idLegalClient: z.number(),
  idCountry: z.number(),
  idBusinessForm: z.number(),
  FirstName: z.string().nullable(),
  LastName: z.string().nonempty(),
  SurName: z.string().nullable().optional(),
  Email: z.string().nullable().optional(),
  Birthdate: z.string().nullable().optional(),
  Birthplace: z.string().nullable().optional(),
  PlaceOfDeath: z.string().nullable().optional(),
  DateOfDeath: z.string().nullable().optional(),
  idGender: z.number().nullable().optional(),
  Gender: z.string().nullable().optional(),
  TaxNumber: z.string().nullable().optional(),
  SalesTaxNumber: z.string().nullable().optional(),
  idAcademicTitle: z.number().nullable().optional(),
  AcademicTitle: z.string().nullable().optional(),
  BusinessForm: z.string().nullable().optional(),
  idLegalClientType: z.number(),
  LegalClientType: z.string(),
  idLegalClientUser: z
    .number()
    .nullable()
    .transform(x => x ?? -1),
  loginPossible: z.boolean(),
  passwordResetRequired: z.boolean(),
  last_login_at: z.string().nullable().optional(),
  isReceivingEmailEnabled: z.boolean(),
  Updated_at: z.string().nullable().optional(),
  Created_at: z.string().nullable().optional(),
  syncWithArnoTop: z.boolean(),
  //LegalClientUser: legalClientUserSchema,
  idArnoTop: z.number().nullable().optional(),
  //syncWithArnotop: z.boolean().optional(),
  LegalClientGroups: z.array(legalClientGroupSchema),

  LastSyncWithArnoTop: z.string().nullable().optional(),
  idArnoTopIntern: z.number().nullable().optional(),
  idLegalRole: z.number().nullable().optional(),
  enableInvoiceReminding: z.boolean().optional(),
  enableCertificateReminding: z.boolean().optional(),

  // Pro-Mandant-Vorgaben der Terminaufforderung (aus der Vorgang<->Mandant-
  // Verknuepfung; kommen als 0/1 bzw. FK-Ids aus der DB).
  RequestClientMeetingSelected: z
    .union([z.number(), z.boolean()])
    .nullable()
    .optional(),
  RequestClientDataSelected: z
    .union([z.number(), z.boolean()])
    .nullable()
    .optional(),
  RequestMeetingType_idMeetingType: z.number().nullable().optional(),
  RequestMeetingLocation_idLocation: z.number().nullable().optional(),
  RequestMeetingNote: z.string().nullable().optional(),

  RegisterNumber: z.string().nullable().optional(),
  idCourt: z.number().nullable().optional(),
  Court: z.string().nullable().optional(),

  // !!! Attr. ohne Attr. in der DB
  syncType: z.number().optional(),
  resetPassword: z.boolean().optional(),
});

export const legalClientFullObjectSchema = legalClientSchema.extend({
  AddressArray: z.array(legalClientAddressSchema),
  ContactInformationArray: z.array(contactInformationSchema),
  AccountingArray: z.array(legalClientAccountingSchema),
  LegalTransactionArray: z.array(legalTransactionObjectSchema),
  RelationArray: z.array(z.lazy(() => legalClientRelationshipSchema)),
  Tasks: z.array(taskFullObjectSchema),
  Documents: z.array(
    z.object({
      idDocument: z.number(),
      idLegalClientDocumentType: z.number(),
      idRelationship: z.number().nullable().optional(),
      idLegalTransaction: z.number().nullable().optional(),
      FileName: z.string(), // weil EnableFullobject=false → flach
      idMIMEType: z.number(), // flach, kein Document-Objekt
    })
  ),
  AssetArray: z.array(z.unknown()).optional(),
});

export type ILegalClient = z.infer<typeof legalClientSchema>;
export type ILegalClientFullObject = z.infer<
  typeof legalClientFullObjectSchema
>;

export const initLegalClient = (
  isLegal: boolean = false
): ILegalClientFullObject => {
  return {
    idLegalClient: -1,
    idCountry: 1,
    idBusinessForm: isLegal ? 8 : 1,
    FirstName: '',
    LastName: '',
    idGender: 4,
    SurName: '',
    Birthdate: '',
    Birthplace: '',
    idLegalClientUser: -1,
    AddressArray: [],
    ContactInformationArray: [],
    AccountingArray: [],
    LegalTransactionArray: [],
    RelationArray: [],
    Tasks: [],
    Documents: [],
    LegalClientGroups: [],
    AssetArray: [],
    idLegalClientType: isLegal ? 2 : 1,
    LegalClientType: '',
    loginPossible: false,
    passwordResetRequired: false,
    isReceivingEmailEnabled: false,
    syncWithArnoTop: false,
    idLegalRole: null,

    RegisterNumber: null,
    idCourt: null,
  };
};

export const transferLegalClientInput = ({
  Birthdate,
  loginPossible,
  isReceivingEmailEnabled,
  passwordResetRequired,
  syncWithArnoTop,
  idArnoTop,
  ...rest
}: ILegalClientFullObject): ILegalClientFullObject => ({
  ...rest,
  Birthdate: Birthdate ? format(new Date(Birthdate), 'yyyy-MM-dd') : undefined,
  loginPossible: Number(loginPossible) !== 0,
  isReceivingEmailEnabled: Number(isReceivingEmailEnabled) !== 0,
  passwordResetRequired: Number(passwordResetRequired) !== 0,
  syncWithArnoTop: syncWithArnoTop || idArnoTop ? true : false,
  RelationArray: [],
  LegalTransactionArray: (rest.LegalTransactionArray ?? []).map(x => ({
    LegalTransaction: x.LegalTransaction,
    idLegalRole: x.idLegalRole ?? null,
    LegalRole: null,
  })),
});
